/*------------------------------------------------------------------------------
   About      : Entity picker (Roles | Users) shared by every Assignments tab

   Extracted from the old EntityPermissionPanel so the same list drives both
   the "Pages & Actions" and "API Permissions" category panels without two
   independent copies of the tab/list state going out of sync.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { Box, Tab, Tabs, Divider, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { RbacEntityType } from 'store/dataReducers/rbacSlice';
import { Role, RbacUser } from 'types/apis/rbacInterfaces';

const dedupeById = <T extends { id: number | string }>(items: T[]): T[] => {
  const seen = new Set<number | string>();
  return items.filter((item) => (seen.has(item.id) ? false : (seen.add(item.id), true)));
};

export interface EntitySidebarProps {
  tab: RbacEntityType;
  roles: Role[];
  users: RbacUser[];
  selectedEntityType?: RbacEntityType;
  selectedEntityId?: number | string;
  onTabChange: (tab: RbacEntityType) => void;
  onSelectEntity: (id: number | string) => void;
}

export const EntitySidebar = ({
  tab,
  roles,
  users,
  selectedEntityType,
  selectedEntityId,
  onTabChange,
  onSelectEntity,
}: EntitySidebarProps) => {

  const entities: Array<{ id: number | string; label: string; secondary?: string }> = tab === 'role'
    ? dedupeById(roles).map((r) => ({
        id: r.id,
        label: r.name,
        secondary: r.feature_groups || r.features
          ? `${r.feature_groups?.length || 0} groups · ${r.features?.length || 0} features`
          : r.description,
      }))
    : dedupeById(users).map((u) => ({
        id: u.id,
        label: u.email,
        secondary: u.roles?.length ? u.roles.map((r) => r.name).join(', ') : undefined,
      }));

  return (
    <Box sx={{ width: 280, flexShrink: 0, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
      <Tabs value={tab} onChange={(_, value) => onTabChange(value)} variant="fullWidth">
        <Tab label="Roles" value="role" />
        <Tab label="Users" value="user" />
      </Tabs>
      <Divider />
      <List sx={{ overflowY: 'auto', flexGrow: 1 }}>
        {entities.map((entity) => (
          <ListItemButton
            key={entity.id}
            selected={selectedEntityType === tab && selectedEntityId === entity.id}
            onClick={() => onSelectEntity(entity.id)}
          >
            <ListItemText
              primary={entity.label}
              secondary={entity.secondary}
              secondaryTypographyProps={{ noWrap: true, fontSize: 12 }}
            />
          </ListItemButton>
        ))}
        {entities.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No {tab === 'role' ? 'roles' : 'users'} found.
          </Typography>
        )}
      </List>
    </Box>
  );
};
